import React, { useEffect, useState, useRef } from 'react';
import { Card, Row, Col, DatePicker, Select, Spin } from 'antd';
import Plotly from 'plotly.js';
import createPlotlyComponent from 'react-plotly.js/factory';
import SensorService from '../../services/sensorService';
import userService from '../../services/userService';
import moment from 'moment';
import './sensorboxStateVisualization.css';

const Plot = createPlotlyComponent(Plotly);

const { RangePicker } = DatePicker;
const { Option } = Select;

interface Props {
  sensorboxName: string;
  sensorboxId?: string;
}

interface StateTransition {
  sensorbox_name: string;
  state: 'offline' | 'online';
  timestamp: number;
  duration_ms?: number;
  metadata?: any;
}

interface Statistics {
  totalOfflineCount: number;
  totalOnlineCount: number;
  totalOfflineDuration: number;
  totalOnlineDuration: number;
  averageOfflineDuration: number;
  longestOfflinePeriod: number;
  longestOnlinePeriod: number;
  uptimePercentage: number;
  timeRange: number;
}

// ─────────────────────────────────────────────
// Speedometer (replaces both old Speedometer
// and AnimatedSpeedometer)
// ─────────────────────────────────────────────
interface AnimatedSpeedometerProps {
  value: number; // 0 – 100
}

const AnimatedSpeedometer: React.FC<AnimatedSpeedometerProps> = ({ value }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const [isVisible, setIsVisible] = useState(false);
  const [animatedNeedle, setAnimatedNeedle] = useState(0);

  const clampedValue = Math.min(Math.max(value, 0), 100);
  const DURATION = 2000;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) setIsVisible(true);
        });
      },
      { threshold: 0.3 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    startTimeRef.current = null;

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / DURATION, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedNeedle(clampedValue * eased);
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setAnimatedNeedle(clampedValue);
      }
    };
    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isVisible, clampedValue]);

  // ── Geometry ──────────────────────────────────────────
  const size = 280;
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.36;
  const START_ANGLE = -220;
  const END_ANGLE = 40;

  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const arcPath = (fromDeg: number, toDeg: number, radius: number) => {
    const s = { x: cx + radius * Math.cos(toRad(fromDeg)), y: cy + radius * Math.sin(toRad(fromDeg)) };
    const e = { x: cx + radius * Math.cos(toRad(toDeg)), y: cy + radius * Math.sin(toRad(toDeg)) };
    const large = toDeg - fromDeg > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${radius} ${radius} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  const angleForValue = (v: number) => START_ANGLE + (v / 100) * (END_ANGLE - START_ANGLE);

  // ── Colors ────────────────────────────────────────────
  const FILL_COLOR = '#22c55e';    // green — completed
  const TRACK_COLOR = '#ef4444';   // red   — remaining
  const NEEDLE_COLOR = '#111111';  // black — needle

  // ── Needle ────────────────────────────────────────────
  const needleAngle = angleForValue(animatedNeedle);
  const needleTip = {
    x: cx + (r - 16) * Math.cos(toRad(needleAngle)),
    y: cy + (r - 16) * Math.sin(toRad(needleAngle)),
  };
  const needleBase1 = {
    x: cx + 9 * Math.cos(toRad(needleAngle + 90)),
    y: cy + 9 * Math.sin(toRad(needleAngle + 90)),
  };
  const needleBase2 = {
    x: cx + 9 * Math.cos(toRad(needleAngle - 90)),
    y: cy + 9 * Math.sin(toRad(needleAngle - 90)),
  };

  const ticks = Array.from({ length: 11 }, (_, i) => i * 10);

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '8px 8px 8px',
        // fontFamily: "'DM Mono', monospace",
      }}
    >

      <svg
        width={size}
        height={size * 0.82}
        viewBox={`0 0 ${size} ${size * 0.82}`}
        style={{ overflow: 'visible' }}
      >
        {/* Track — red (remaining) */}
        <path
          d={arcPath(START_ANGLE, END_ANGLE, r)}
          fill="none"
          stroke={TRACK_COLOR}
          strokeWidth="13"
          strokeLinecap="round"
        />

        {/* Filled arc — green (completed) */}
        {clampedValue > 0 && (
          <path
            d={arcPath(START_ANGLE, angleForValue(clampedValue), r)}
            fill="none"
            stroke={FILL_COLOR}
            strokeWidth="13"
            strokeLinecap="round"
          />
        )}

        {/* Tick marks */}
        {ticks.map((t) => {
          const a = toRad(angleForValue(t));
          const inner = r - 20;
          const outer = r - 7;
          return (
            <line
              key={t}
              x1={cx + inner * Math.cos(a)} y1={cy + inner * Math.sin(a)}
              x2={cx + outer * Math.cos(a)} y2={cy + outer * Math.sin(a)}
              stroke="#888"
              strokeWidth="2"
            />
          );
        })}

        {/* Tick labels */}
        {ticks.filter((_, i) => i % 2 === 0).map((t) => {
          const a = toRad(angleForValue(t));
          const labelR = r - 34;
          return (
            <text
              key={t}
              x={cx + labelR * Math.cos(a)}
              y={cy + labelR * Math.sin(a)}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#888"
              fontSize="8.5"
              fontFamily="DM Mono, monospace"
            >
              {t}
            </text>
          );
        })}

        {/* Needle — black */}
        <polygon
          points={`${needleTip.x},${needleTip.y} ${needleBase1.x},${needleBase1.y} ${needleBase2.x},${needleBase2.y}`}
          fill={NEEDLE_COLOR}
        />
        <circle cx={cx} cy={cy} r="10" fill="#222" stroke="#444" strokeWidth="2" />
        <circle cx={cx} cy={cy} r="4" fill={NEEDLE_COLOR} />
      </svg>

      {/* Label */}
      <div style={{
        fontSize: '11px',
        color: '#888',
        letterSpacing: '1px',
        marginTop: '4px',
        // fontFamily: 'DM Mono, monospace',
        textTransform: 'uppercase',
      }}>
       Total Online Time 
      </div>

      {/* Value */}
      <div style={{
        fontSize: '36px',
        fontWeight: 600,
        color: "rgb(79, 140, 146)",
        // fontFamily: 'DM Mono, monospace',
        // fontVariantNumeric: 'tabular-nums',
        marginTop: '6px',
      }}>
        {clampedValue.toFixed(2)}%
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Main component (unchanged below this line)
// ─────────────────────────────────────────────
const SensorboxStateVisualization: React.FC<Props> = ({ sensorboxName, sensorboxId }) => {
  const [loading, setLoading] = useState(true);
  const [transitions, setTransitions] = useState<StateTransition[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [dateRange, setDateRange] = useState<[moment.Moment, moment.Moment]>([
    moment().subtract(30, 'days'),
    moment()
  ]);
  const [activeView, setActiveView] = useState<'statistics' | 'chart'>('statistics');

  useEffect(() => {
    if (sensorboxName) {
      fetchData();
    }
  }, [sensorboxName, dateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const startDate = dateRange[0].valueOf();
      const endDate = dateRange[1].valueOf();

      const [transitionsRes, statisticsRes] = await Promise.all([
        SensorService.getSensorboxStateTransitions(sensorboxName, 1, 1000, startDate, endDate),
        SensorService.getSensorboxStateStatistics(sensorboxName, startDate, endDate)
      ]);

      const transitionsList = transitionsRes.data.data?.transitions || [];
      transitionsList.sort((a: StateTransition, b: StateTransition) => b.timestamp - a.timestamp);
      setTransitions(transitionsList);
      setStatistics(statisticsRes.data.data || null);
    } catch (error) {
      console.error('Error fetching state data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (ms: number): string => {
    if (ms < 60000) {
      return `${Math.round(ms / 1000)}s`;
    } else if (ms < 3600000) {
      const minutes = Math.floor(ms / 60000);
      const seconds = Math.round((ms % 60000) / 1000);
      return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    } else if (ms < 86400000) {
      const hours = Math.floor(ms / 3600000);
      const minutes = Math.round((ms % 3600000) / 60000);
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    } else {
      const days = Math.floor(ms / 86400000);
      const hours = Math.round((ms % 86400000) / 3600000);
      return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }
  };

  const prepareTransitionChartData = () => {
    if (!transitions || transitions.length === 0) return [];

    const sortedTransitions = [...transitions].sort((a, b) => a.timestamp - b.timestamp);
    const startTime = dateRange[0].valueOf();
    const endTime = dateRange[1].valueOf();

    const dataPoints: Array<{
      timestamp: number;
      state: number;
      label: string;
      duration: number;
      durationFormatted: string;
    }> = [];

    sortedTransitions.forEach((transition, index) => {
      if (transition.timestamp >= startTime && transition.timestamp <= endTime) {
        let duration = transition.duration_ms;
        let durationFormatted = '';

        if (duration !== undefined && duration !== null) {
          durationFormatted = formatDuration(duration);
        } else if (index === sortedTransitions.length - 1) {
          duration = Date.now() - transition.timestamp;
          durationFormatted = formatDuration(duration);
        } else {
          const nextTransition = sortedTransitions[index + 1];
          if (nextTransition) {
            duration = nextTransition.timestamp - transition.timestamp;
            durationFormatted = formatDuration(duration);
          } else {
            duration = 0;
            durationFormatted = formatDuration(0);
          }
        }

        dataPoints.push({
          timestamp: transition.timestamp,
          state: transition.state === 'online' ? 1 : 0,
          label: moment(transition.timestamp).format('MMM DD, YYYY HH:mm'),
          duration: duration || 0,
          durationFormatted: durationFormatted || '0s',
        });
      }
    });

    return dataPoints;
  };

  const getTimeFormat = () => {
    const daysDiff = dateRange[1].diff(dateRange[0], 'days');
    if (daysDiff < 1) return '%H:%M';
    if (daysDiff < 7) return '%b %d %H:%M';
    if (daysDiff < 30) return '%b %d';
    return '%b %d, %Y';
  };

  const handleCopy = async () => {
    try {
      const plotElement = document.querySelector('.js-plotly-plot');
      if (!plotElement) { alert('Plot not found!'); return; }
      const imageData = await Plotly.toImage(plotElement as any, { format: 'png', height: 400, width: 1200, scale: 3 });
      const response = await fetch(imageData);
      const blob = await response.blob();
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    } catch (error) {
      console.error('Clipboard copy failed:', error);
      alert('Copy failed');
    }
  };

  const handleEmailPlot = async () => {
    try {
      const plotElement = document.querySelector('.js-plotly-plot');
      if (!plotElement) { alert('Plot not found!'); return; }

      let to_email = '';
      let cc_email = '';

      if (sensorboxId) {
        try {
          const users = await userService.getUsersBySensors(sensorboxId);
          to_email = users.contractorDetails?.map((u: any) => u.email).filter(Boolean).join(', ') || '';
          cc_email = users.consumerDetails?.map((u: any) => u.email).filter(Boolean).join(', ') || '';
        } catch (error) {
          console.error('Error fetching users:', error);
        }
      }

      const subject = encodeURIComponent(`${sensorboxName} - State Transitions Chart`);
      const body = encodeURIComponent('Hi,\n\nPlease find the state transitions chart attached below.\n\n--\nBest Regards,\nMechasense Team');
      window.open(`mailto:${to_email}?cc=${cc_email}&subject=${subject}&body=${body}`);
    } catch (error) {
      console.error('Email plot failed:', error);
      alert('Failed to email plot.');
    }
  };

  const customCopyIcon = {
    width: 524, height: 524,
    path: 'M408 112H184c-13.2 0-24 10.8-24 24v32h48v-16h176v176h-16v48h32c13.2 0 24-10.8 24-24V136c0-13.2-10.8-24-24-24zM328 192H104c-13.2 0-24 10.8-24 24v208c0 13.2 10.8 24 24 24h224c13.2 0 24-10.8 24-24V216c0-13.2-10.8-24-24-24zM320 416H112V224h208v192z'
  };

  const customMailIcon = {
    width: 512, height: 512,
    path: 'M467 61H45C20.2 61 0 81.2 0 106v300c0 24.8 20.2 45 45 45h422c24.8 0 45-20.2 45-45V106c0-24.8-20.2-45-45-45zM256 295L45 151v-5h422v5L256 295z'
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="sensorbox-state-visualization">
      <Card>
        <Row gutter={16} className="mb-3 d-flex justify-content-between">
          <Col span={18}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                }
              }}
              format="MMM DD, YYYY"
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={6}>
            <Select value={activeView} onChange={setActiveView} style={{ width: '100%' }}>
              <Option value="statistics">Statistics</Option>
              <Option value="chart">Chart</Option>
            </Select>
          </Col>
        </Row>

        {activeView === 'statistics' && statistics && (
          <div className="statistics-view">
            {/* Offline Details */}
            <div className="statistics-section">
              <h3 className="section-header" style={{ marginBottom: '8px', color: '#4F8C92', fontSize: '18px', fontWeight: 'bold' }}>
                Offline Details
              </h3>
              <Row gutter={16}>
                <Col span={6}>
                  <Card className="stat-card">
                    <div className="stat-label">Total Offline Events</div>
                    <div className="stat-value">{statistics.totalOfflineCount}</div>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card className="stat-card">
                    <div className="stat-label">Total Offline Time</div>
                    <div className="stat-value">{formatDuration(statistics.totalOfflineDuration)}</div>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card className="stat-card">
                    <div className="stat-label">Average Offline Duration</div>
                    <div className="stat-value">{formatDuration(statistics.averageOfflineDuration)}</div>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card className="stat-card">
                    <div className="stat-label">Longest Offline Period</div>
                    <div className="stat-value">{formatDuration(statistics.longestOfflinePeriod)}</div>
                  </Card>
                </Col>
              </Row>
            </div>

            {/* Online Details */}
            <div className="statistics-section" style={{ marginTop: '8px' }}>
              <h3 className="section-header" style={{ marginBottom: '8px', color: '#4F8C92', fontSize: '18px', fontWeight: 'bold' }}>
                Online Details
              </h3>
              <Row gutter={16}>
                <Col span={12}>
                  <Card className="stat-card">
                    <div className="stat-label">Total Online Time</div>
                    <div className="stat-value">{formatDuration(statistics.totalOnlineDuration)}</div>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card className="stat-card">
                    <div className="stat-label">Longest Online Period</div>
                    <div className="stat-value">{formatDuration(statistics.longestOnlinePeriod || 0)}</div>
                  </Card>
                </Col>
              </Row>
            </div>

            {/* Network Uptime */}
            <div className="statistics-section" style={{ marginTop: '8px' }}>
              <h3 className="section-header" style={{ marginBottom: '8px', color: '#4F8C92', fontSize: '18px', fontWeight: 'bold' }}>
                Network Up time
              </h3>
              <Row gutter={16}>
                <Col span={24}>
                  <Card className="stat-card" style={{ textAlign: 'center' }}>
                    <AnimatedSpeedometer value={statistics.uptimePercentage} />
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        )}

        {activeView === 'chart' && (
          <div className="chart-view">
            <h4>State Transitions</h4>
            {(() => {
              const chartData = prepareTransitionChartData();
              if (chartData.length === 0) {
                return (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No transition data available for the selected period
                  </div>
                );
              }
              return (
                <Plot
                  data={[
                    {
                      x: chartData.map(item => item.timestamp),
                      y: chartData.map(item => item.state),
                      type: 'scatter',
                      mode: 'lines+markers',
                      line: { color: '#67ABB2', width: 2 },
                      marker: {
                        size: 8,
                        color: chartData.map(item => item.state === 1 ? '#52c41a' : '#ff4d4f'),
                        line: { color: '#fff', width: 1 }
                      },
                      name: 'State',
                      hovertemplate: chartData.map((item) => {
                        const duration = item.durationFormatted || formatDuration(item.duration || 0);
                        return `<b style="font-size: 14px; color: #333;">${item.label}</b><br><span style="font-size: 13px; color: #666; font-weight: bold;">Duration: </span><span style="font-size: 13px; color: #333; font-weight: bold;">${duration}</span><extra></extra>`;
                      }),
                      text: chartData.map(item => item.label),
                      customdata: chartData.map(item => item.durationFormatted || 'N/A')
                    }
                  ]}
                  layout={{
                    xaxis: {
                      type: 'date',
                      title: { text: 'Time' },
                      showgrid: true,
                      gridcolor: '#e0e0e0',
                      gridwidth: 1,
                      tickformat: getTimeFormat()
                    },
                    yaxis: {
                      title: { text: 'State' },
                      tickvals: [0, 1],
                      ticktext: ['Offline', 'Online'],
                      range: [-0.1, 1.1],
                      showgrid: true,
                      gridcolor: '#e0e0e0',
                      gridwidth: 1
                    },
                    margin: { l: 60, r: 20, t: 20, b: 60 },
                    height: 400,
                    hovermode: 'closest',
                    hoverlabel: {
                      bgcolor: 'rgba(255, 255, 255, 0.95)',
                      bordercolor: '#ddd',
                      font: { color: '#333', size: 13 }
                    }
                  } as any}
                  config={{
                    scrollZoom: false,
                    displaylogo: false,
                    responsive: true,
                    displayModeBar: true,
                    modeBarButtonsToAdd: [
                      { title: 'Copy plot', name: 'Copy plot', icon: customCopyIcon, click: handleCopy },
                      { title: 'Email plot', name: 'Email plot', icon: customMailIcon, click: handleEmailPlot }
                    ]
                  }}
                  style={{ width: '100%', height: '100%' }}
                />
              );
            })()}
          </div>
        )}
      </Card>
    </div>
  );
};

export default SensorboxStateVisualization;